import {useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {useCallback, useEffect, useRef, useState} from 'react';

import {FeaturesEnum, HomeEnum, QueryParamsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {useNavigate} from 'react-router-dom';
import {HEADER_TITLES} from '../../constants/headerTitles';
import {SEARCH_BAR_PLACEHOLDER} from '../../constants/searchBarPlaceholder';
import {
	EXAMS_LABELS,
	EXAMS_LABELS_ADVANCE_DIAGNOSTICS,
	EXAMS_LABELS_WITHOUT_DISTRICT,
} from '../../constants/stepLabels';
import {getExamStepPosition} from '../../utils/getExamStepPosition';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useBackButtonBooking} from 'presentation/modules/booking/shared/hooks/useBackButtonBooking';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useFeatureFlagsQuery} from 'presentation/queries/useFeatureFlagsQuery';
import {useIsAdvanceDiagnosticsExamsJourney} from '../useIsAdvanceDiagnosticsExamsJourney';

const {bookExams} = translations['pt-br'].privateFlow.services;

export function useExamBooking() {
	const stepsVisited = useRef(new Set<number>());
	const [isFirstVisit, setIsFirstVisit] = useState(true);
	const {isMobile, isLaptop, isDesktop} = useMediaQueries();
	const hideSearchBar = useHideSearchBarStore(state => state.hideSearchBar);
	const setHideSearchBar = useHideSearchBarStore(state => state.setHideSearchBar);
	const hideStepBar = useHideStepBarStore(state => state.hideStepBar);
	const setHideStepBar = useHideStepBarStore(state => state.setHideStepBar);
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);

	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);
	const {isEnabled: isAdvanceDiagnosticsFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PPO_ADVANCE_DIAGNOSTICS_EXAMS_JOURNEY,
	);
	const {isFetched: areFeatureFlagsFetched} = useFeatureFlagsQuery();
	const isAdvanceDiagnosticsFlow = useIsAdvanceDiagnosticsExamsJourney();
	const navigate = useNavigate();
	const {
		activeBookingFlowStep,
		bookingData,
		setActiveBookingFlowStep,
		setBookingData,
		setSearchParams,
		clearSearch,
	} = useQueryParamsSync({isFirstVisit});

	useBackButtonBooking({flowType: BookingTypeEnum.EXAM});

	const isUsingGeolocation = !bookingData.isManualLocation;

	const {
		handleChangeModals,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
	} = useBookingSummary();

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		DISTRICT_STEP,
	} = getExamStepPosition({
		isManualLocation: bookingData.isManualLocation,
	});

	const getHeaderTitle = () => {
		if (!isMobile) return bookExams;

		return isUsingGeolocation
			? HEADER_TITLES.withGeo[activeBookingFlowStep]
			: HEADER_TITLES.withoutGeo[activeBookingFlowStep];
	};

	const isAdvanceDiagnosticsRebookingStart =
		isAdvanceDiagnosticsFlow &&
		Boolean(bookingData.isRebooking) &&
		activeBookingFlowStep === PROFESSIONAL_STEP;
	const shouldHideBackBtn =
		(bookingData.isRebooking && activeBookingFlowStep === DISTRICT_STEP) ||
		isAdvanceDiagnosticsRebookingStart;
	const advanceDiagnosticsSteps = [SPECIALITY_OR_EXAM_STEP, PROFESSIONAL_STEP, REVIEW_STEP];

	const getStepLabels = () => {
		if (isAdvanceDiagnosticsFlow) return EXAMS_LABELS_ADVANCE_DIAGNOSTICS;

		return isUsingGeolocation ? EXAMS_LABELS_WITHOUT_DISTRICT : EXAMS_LABELS;
	};

	const stepLabels = getStepLabels();
	const stepperActiveStep = isAdvanceDiagnosticsFlow
		? Math.max(advanceDiagnosticsSteps.indexOf(activeBookingFlowStep), 0)
		: activeBookingFlowStep;
	const searchBarPlaceholder = isUsingGeolocation
		? SEARCH_BAR_PLACEHOLDER.withGeo
		: SEARCH_BAR_PLACEHOLDER.withoutGeo;

	const isLastStep = activeBookingFlowStep === REVIEW_STEP;
	const shouldHideSearchBarAndBookingSummary = activeBookingFlowStep === LOCATION_STEP;

	const shouldHideSearchBar = hideSearchBar || isLastStep || shouldHideSearchBarAndBookingSummary;

	const shouldUseNewResumeLayout = isNewResumePageEnabled && isLastStep;
	const showExamSummarySectionGroupInGrid =
		isDesktop && !shouldHideSearchBarAndBookingSummary && !shouldUseNewResumeLayout;

	const handleClose = useCallback(() => {
		setAbandonFlowState(true);
	}, []);

	const handleBack = useCallback(() => {
		clearSearch();

		if (
			activeBookingFlowStep === LOCATION_STEP ||
			(isAdvanceDiagnosticsFlow && activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) ||
			isAdvanceDiagnosticsRebookingStart
		) {
			handleClose();
			return;
		}

		if (isAdvanceDiagnosticsFlow) {
			if (activeBookingFlowStep === PROFESSIONAL_STEP) {
				setBookingData(prev => ({...prev, dateAt: ''}));
				setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
				return;
			}

			if (activeBookingFlowStep === REVIEW_STEP) {
				setBookingData(prev => ({
					...prev,
					hourAt: '',
					dateAt: '',
					professionalCode: '',
					professionalName: '',
				}));

				setSearchParams(
					prev => {
						prev.delete(QueryParamsEnum.SHIFT);
						prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

						return prev;
					},
					{
						replace: true,
					},
				);

				setActiveBookingFlowStep(PROFESSIONAL_STEP);
				return;
			}
		}

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setBookingData(prev => ({
				...prev,
				isManualLocation: true,
			}));
		}

		if (activeBookingFlowStep === DISTRICT_STEP) {
			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				isScheduleScript: false,
				districtName: '',
				allDistrictsSelected: false,
				citiesNames: '',
			}));
		}

		if (activeBookingFlowStep === UNIT_STEP) {
			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
					return prev;
				},
				{
					replace: true,
				},
			);
		}

		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitAddress: '',
				unitName: '',
				dateAt: '',
			}));
		}

		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

					return prev;
				},
				{
					replace: true,
				},
			);
		}

		setActiveBookingFlowStep(value => value - 1);
	}, [activeBookingFlowStep, isAdvanceDiagnosticsFlow, isAdvanceDiagnosticsRebookingStart]);

	const handleConfirmExitFlow = useCallback(() => {
		setAbandonFlowState(false);
		navigate(HomeEnum.HOME_PAGE);
	}, []);

	const showSwipeableDrawerBookingSummary =
		!isLastStep && isLaptop && !shouldHideSearchBarAndBookingSummary;

	const customFooterMarginBottom = () => {
		if (isUsingGeolocation) return undefined;
		if (activeBookingFlowStep === DISTRICT_STEP && isMobile) {
			return '142px';
		}

		return undefined;
	};

	const shouldResetUnitResponseState = [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
	].includes(activeBookingFlowStep);

	const handleNext = () => {
		if (activeBookingFlowStep === stepLabels.length) {
			return;
		}
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});
		if (shouldResetUnitResponseState) {
			resetUnitResponseState();
		}
		if (isUsingGeolocation && activeBookingFlowStep < UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				hasGeoDistrictsFilter: false,
			}));
		}

		if (isAdvanceDiagnosticsFlow && activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setActiveBookingFlowStep(PROFESSIONAL_STEP);
			return;
		}

		setActiveBookingFlowStep(value => value + 1);
	};

	useEffect(() => {
		const hasStaleAdvanceDiagnosticsContext =
			areFeatureFlagsFetched &&
			!isAdvanceDiagnosticsFlagEnabled &&
			bookingData.isAdvanceDiagnosticsFlow;

		if (hasStaleAdvanceDiagnosticsContext) {
			setBookingData(prev => ({...prev, isAdvanceDiagnosticsFlow: false}));
		}
	}, [areFeatureFlagsFetched, isAdvanceDiagnosticsFlagEnabled, bookingData.isAdvanceDiagnosticsFlow]);

	useEffect(() => {
		const isFirstVisitValue = !stepsVisited.current.has(activeBookingFlowStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeBookingFlowStep);
	}, [activeBookingFlowStep]);

	useEffect(() => {
		setHideSearchBar(false);
		setHideStepBar(false);
		clearSearch();
	}, [activeBookingFlowStep]);

	return {
		getHeaderTitle,
		shouldHideBackBtn,
		stepLabels,
		searchBarPlaceholder,
		isLastStep,
		shouldHideSearchBar,
		handleClose,
		handleBack,
		handleConfirmExitFlow,
		showSwipeableDrawerBookingSummary,
		customFooterMarginBottom,
		handleNext,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
		handleChangeModals,
		isMobile,
		hideStepBar,
		setActiveBookingFlowStep,
		activeBookingFlowStep,
		stepperActiveStep,
		shouldHideSearchBarAndBookingSummary,
		isNewResumePageEnabled,
		shouldUseNewResumeLayout,
		showExamSummarySectionGroupInGrid,
	};
}
